import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, Renderer2, ViewChild, inject } from '@angular/core';
import { TreatmentCategoryComponent } from '@app/salon-details/treatment-category/treatment-category.component';
import { ITreatmentApiModel } from '@app/shared/models';
import { TranslateModule } from '@ngx-translate/core';
import { Subscription, fromEvent, debounceTime } from 'rxjs';

@Component({
  selector: 'app-multiple-treatments-basket',
  templateUrl: './multiple-treatments-basket.component.html',
  styleUrls: ['./multiple-treatments-basket.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    TreatmentCategoryComponent]
})
export class MultipleTreatmentsBasketComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('containerOfTreatments', { static: true }) containerOfTreatmentsElementRef: ElementRef;
  @Input() selectedTreatments: ITreatmentApiModel[] = [];
  @Output() bookMultpleTreatmentsClicked = new EventEmitter<ITreatmentApiModel[]>();
  @ViewChild('listOftreatments', { static: true }) listOfTreatmentsElementRef: ElementRef;
  private readonly rerender = inject(Renderer2);
  private readonly elementRef = inject(ElementRef);
  private resize$ = fromEvent(window, 'resize');
  private resizeSub = new Subscription();
  private recalculateAnimation = new Subscription();
  private listWidthObserver = new ResizeObserver(() => this.assignElementRefWidth());
  private containerWidthObserver = new ResizeObserver(() => this.assignElementRefWidth());
  private listOfTreatmentsWidth: number;
  private containerOfTreatmentsWidth: number;
  private readonly animationSpeed = 140;

  ngOnInit(): void {
    this.resizeSub = this.resize$.pipe(
      debounceTime(800)).subscribe(() => {
        this.calculateAnimationTime();
      });
  }

  ngAfterViewInit(): void {
    this.listOfTreatmentsElementRef?.nativeElement && this.listWidthObserver.observe(this.listOfTreatmentsElementRef.nativeElement);
    this.containerOfTreatmentsElementRef?.nativeElement && this.containerWidthObserver.observe(this.containerOfTreatmentsElementRef.nativeElement);
  }

  ngOnDestroy(): void {
    this.resizeSub.unsubscribe();
    this.recalculateAnimation.unsubscribe();
    this.listOfTreatmentsElementRef?.nativeElement && this.listWidthObserver.unobserve(this.listOfTreatmentsElementRef.nativeElement);
    this.containerOfTreatmentsElementRef?.nativeElement && this.containerWidthObserver.unobserve(this.containerOfTreatmentsElementRef.nativeElement);
  }

  get listOfTreatmentsNames(): string {
    return this.selectedTreatments.map((treatment) => treatment.name).join(', ');
  }

  get numberOfTreatments(): number {
    return this.selectedTreatments.length;
  }

  public bookTreatments(selectedTreatments: ITreatmentApiModel[]): void {
    this.bookMultpleTreatmentsClicked.next(selectedTreatments);
  }

  public calculateAnimationTime(): void {
    const ratio = this.containerOfTreatmentsWidth / this.listOfTreatmentsWidth;
    const calculatedTime = ((this.listOfTreatmentsWidth / this.animationSpeed) / ratio).toString();
    this.elementRef.nativeElement.style.setProperty('--dvi-basket-dynamic-animation-time', `${calculatedTime}s`);
    if (this.containerOfTreatmentsWidth < this.listOfTreatmentsWidth) {
      this.rerender.addClass(this.listOfTreatmentsElementRef.nativeElement, 'animate');
    } else if (this.containerOfTreatmentsWidth >= this.listOfTreatmentsWidth) {
      this.rerender.removeClass(this.listOfTreatmentsElementRef.nativeElement, 'animate');
    }
  }

  private assignElementRefWidth(): void {
    this.listOfTreatmentsWidth = this.listOfTreatmentsElementRef.nativeElement.offsetWidth;
    this.containerOfTreatmentsWidth = this.containerOfTreatmentsElementRef.nativeElement.offsetWidth;
    this.calculateAnimationTime();
  }
}
